import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Box, Card, CardContent, Grid, Typography } from '@mui/material'

import SkeletonLoader from '@/views/components/skeleton'
import {
  PersonalizationEngineChartType,
  PersonalizationEngineType,
} from '@/state/personalization-engine/models'
import NoContentAvailable from '@/views/components/no-content-available/index.component'

import { ApplicationState, PaginationResponse } from '@/state/root.models'
import { colors } from '@/theme/colors.theme'
import { TransactionTypePayment } from '@/enums'

import PaginationTable from '@/views/components/pagination-table/index.component'
import { ChartTableComponent } from './chart-table.component'
import { PEPieChartComponent } from '../pie-chart'

interface Props {
  data: PersonalizationEngineType[]
  paginatedRows: PersonalizationEngineType[]
  title: string
  isLoading: boolean
  pagination: PaginationResponse
  handleChangeLimit: (newLimit: number) => void
  handleChangePage: (newPage: number) => void
}

export const ChartCardComponent = ({
  data,
  paginatedRows,
  title,
  isLoading,
  pagination,
  handleChangeLimit,
  handleChangePage,
}: Props) => {
  const { t } = useTranslation()

  const { filters } = useSelector(
    ({ personalizationEngine }: ApplicationState) => personalizationEngine,
  )

  const getFilterData = (
    item: PersonalizationEngineType,
  ): PersonalizationEngineChartType => {
    switch (
      filters.transactionTypePayment
        ? filters.transactionTypePayment.toString()
        : 'all'
    ) {
      case 'all':
        return {
          name: item.name,
          percentage: Number(item.percentage),
          turnover: parseFloat(item.amount),
        }

      case `${TransactionTypePayment.BONUS_MONEY}`:
        return {
          name: item.name,
          percentage: Number(item.percentage_bonus_money),
          turnover: parseFloat(item.amount_bonus_money),
        }

      case `${TransactionTypePayment.REAL_MONEY}`:
        return {
          name: item.name,
          percentage: Number(item.percentage_real_money),
          turnover: parseFloat(item.amount_real_money),
        }

      default:
        return {
          name: item.name,
          percentage: Number(item.percentage),
          turnover: parseFloat(item.amount),
        }
    }
  }

  const filterData = data.map((el) => getFilterData(el))
  const tableData = paginatedRows.map((el) => getFilterData(el))

  return (
    <Card
      sx={{
        backgroundColor: colors.paper2,
        height: '100%',
        borderRadius: '16px',
      }}
    >
      <CardContent sx={{ backgroundColor: colors.paper2, height: '100%' }}>
        <Box mb={filterData.length > 0 ? 3 : 0} mt={1} ml={1}>
          <Typography variant="h6" fontWeight={700}>
            {title}
          </Typography>
        </Box>
        {!isLoading ? (
          filterData.length > 0 ? (
            <Grid container>
              <Grid item xs={12} md={12} lg={4}>
                <Box display="flex" justifyContent="center">
                  <PEPieChartComponent chartData={filterData} />
                </Box>
              </Grid>
              <Grid item xs={12} md={12} lg={8}>
                <ChartTableComponent chartData={tableData} />
                <PaginationTable
                  pagination={pagination}
                  handleChangePage={handleChangePage}
                  handleChangeLimit={handleChangeLimit}
                  small
                  rowsPerPageOptions={[5, 10]}
                />
              </Grid>
            </Grid>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
              flexGrow={1}
              flexDirection="column"
              pb={3}
            >
              <NoContentAvailable
                title={t('messages.noData')}
                instruction={t('messages.noResultsFound')}
                subInstruction={t('messages.tryRemovingFilters')}
              />
            </Box>
          )
        ) : (
          <Box mt={7}>
            <SkeletonLoader element="small-table" />
          </Box>
        )}
      </CardContent>
    </Card>
  )
}
